import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
/**
 * TestError component immediately throws an error when rendered.
 * Useful for verifying ErrorBoundary behavior in the app.
 *
 * @component
 * @throws {Error} Always throws `new Error("A custom error message.")`.
 */
export function TestError() {
	throw new Error('A custom error message.')
}
/**
 * ErrorBoundary component catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} [props.children] – The child components to wrap.
 * @returns {JSX.Element} Either the wrapped children (if no error) or a fallback UI.
 */
class ErrorBoundary extends React.Component {
	/**
	 * Initializes the ErrorBoundary state.
	 *
	 * @constructor
	 * @param {Object} props – React props.
	 */
	constructor(props) {
		super(props)
		this.state = { error: null, errorInfo: null }
	}

	/**
	 * Lifecycle method invoked after an error has been thrown by a descendant component.
	 * It updates the local state to trigger the fallback UI and logs the error.
	 *
	 * @param {Error} error – The error that was thrown.
	 * @param {Object} errorInfo – Additional info about the error (component stack).
	 */
	componentDidCatch(error, errorInfo) {
		// Catch errors in any components below and re-render with error message
		this.setState({
			error,
			errorInfo
		})
		// You can also log error messages to an error reporting service here
		console.error('Error:', error)
	}

	/**
	 * Renders either the child components (if no error) or a fallback UI.
	 *
	 * @returns {JSX.Element}
	 */
	render() {
		if (this.state.error) {
			// Error path
			return (
				<div className="section-error-bounderies we-error-boundary">
					<h2 className="header">Ups...</h2>
					<p className="text">
						Unexpected error occurred.
						<br></br>
						Administrator was informed.
					</p>
					<Link className="btn-style-1" to=".">
						Home
					</Link>
				</div>
			)
		}
		// Normally, just render children
		return this.props.children
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node
}

export { ErrorBoundary }
